import { useEffect, useState } from "react";
import { finAPI, fmtINR, fmtDate, STATUS_STYLE } from "./finApi";
import { Plus, X, Gift, Filter, Check } from "lucide-react";

const CATEGORIES = [
  ["room", "Room"], ["meal", "Meal"], ["laundry", "Laundry"],
  ["amenity", "Amenity"], ["spa", "Spa"], ["pool", "Pool"], ["other", "Other"],
];
const REASON_CODES = [
  ["vip", "VIP"], ["loyalty", "Loyalty"], ["service_recovery", "Service recovery"],
  ["manager", "Manager"], ["corporate", "Corporate"], ["long_stay", "Long stay"], ["other", "Other"],
];

function CreateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    category: "room", item_name: "", estimated_cost: "",
    folio_id: "", reservation_id: "", reason_code: "vip", reason: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await finAPI.createComp(body);
      onCreated(); onClose();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fin-create-comp-dialog">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">Grant Complimentary</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400 hover:text-slate-700"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                data-testid="fin-comp-category"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] bg-white">
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Estimated Cost (₹) *</label>
              <input value={form.estimated_cost} onChange={e => setForm({...form, estimated_cost: e.target.value})}
                required pattern="^\d+(\.\d{1,2})?$" data-testid="fin-comp-cost"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Item Name *</label>
            <input value={form.item_name} onChange={e => setForm({...form, item_name: e.target.value})}
              required data-testid="fin-comp-item"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Reason Code</label>
              <select value={form.reason_code} onChange={e => setForm({...form, reason_code: e.target.value})}
                data-testid="fin-comp-reason-code"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] bg-white">
                {REASON_CODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Folio ID</label>
              <input value={form.folio_id} onChange={e => setForm({...form, folio_id: e.target.value})}
                data-testid="fin-comp-folio"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] font-mono"/>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Reason *</label>
            <textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})}
              rows={3} required data-testid="fin-comp-reason"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
          </div>
          {err && <div className="text-rose-700 text-[12px] bg-rose-50 px-3 py-2 rounded-lg">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[13px] font-medium text-slate-600">Cancel</button>
            <button type="submit" disabled={busy} data-testid="fin-comp-submit"
              className="px-5 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium disabled:opacity-60">
              {busy ? "Submitting…" : "Grant"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function FinanceComps() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => finAPI.comps(status ? { status } : {}).then(setItems).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (c) => {
    const notes = window.prompt("Approval notes (optional):") ?? "";
    try { await finAPI.approveComp(c.id, { notes }); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="fin-comps-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[12px] text-slate-500"><Filter className="w-4 h-4"/> Filter:</div>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="fin-comp-filter"
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] bg-white">
          <option value="">All statuses</option>
          <option value="requested">Pending</option>
          <option value="approved">Approved</option>
        </select>
        <div className="flex-1"/>
        <button onClick={() => setOpen(true)} data-testid="fin-new-comp-btn"
          className="px-4 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[12px] font-medium inline-flex items-center gap-2 hover:brightness-110">
          <Plus className="w-4 h-4"/> Grant Comp
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center" data-testid="fin-comps-empty">
            <Gift className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
            <div className="text-[13px] text-slate-500">No complimentary services yet.</div>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Code</th>
                <th className="text-left px-4 py-2.5">Category</th>
                <th className="text-left px-4 py-2.5">Item</th>
                <th className="text-right px-4 py-2.5">Cost</th>
                <th className="text-left px-4 py-2.5">Reason</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Requested</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(c => {
                const s = STATUS_STYLE[c.status] || { chip: "bg-slate-100 text-slate-700", label: c.status };
                return (
                  <tr key={c.id} className="hover:bg-slate-50" data-testid={`fin-comp-row-${c.code}`}>
                    <td className="px-4 py-2.5 font-mono text-[12px] text-hocp-navy">{c.code}</td>
                    <td className="px-4 py-2.5"><span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium bg-violet-100 text-violet-700">{c.category}</span></td>
                    <td className="px-4 py-2.5 text-slate-700">{c.item_name}</td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums text-hocp-navy">{fmtINR(c.estimated_cost)}</td>
                    <td className="px-4 py-2.5 text-slate-700"><span className="uppercase text-[10px] font-semibold text-slate-500 mr-2">{c.reason_code}</span>{c.reason}</td>
                    <td className="px-4 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${s.chip}`}>{s.label}</span></td>
                    <td className="px-4 py-2.5 text-slate-500 text-[12px]">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {c.status === "requested" && (
                        <button onClick={() => approve(c)} data-testid={`fin-comp-approve-${c.code}`}
                          className="text-[12px] text-emerald-700 hover:text-emerald-900 font-medium inline-flex items-center gap-1">
                          <Check className="w-3.5 h-3.5"/>Approve
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <CreateDialog open={open} onClose={() => setOpen(false)} onCreated={load}/>
    </div>
  );
}
